import { FC, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { fetchQuery } from "relay-runtime";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";

import {
  MIN_AGE_DAYS,
  MIN_VALUE_USD_SCALED,
} from "../../algorithmic/constants";
import { formatBigintPercent } from "../../common/bigint";
import {
  getAnalyticsEnvironment,
  isAnalyticsConfigured,
} from "../../services/relayAnalytics";
import cclPromo from "../assets/ccl-promo.png";
import { topAprQuery } from "../analytics/AutomatedTradingPromo";
import { AutomatedTradingPromoQuery } from "../analytics/__generated__/AutomatedTradingPromoQuery.graphql";

const TOUR_SEEN_KEY = "rujira-trade-header-tour-seen";

// Avoid re-querying the promo value on every trade-page navigation.
const TOP_APR_CACHE_KEY = "rujira-trade-header-top-apr-v3";
const TOP_APR_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

type TopAprCacheRecord = { apr: string | null; fetchedAt: number };

const readTopAprCache = (): TopAprCacheRecord | null => {
  try {
    const raw = localStorage.getItem(TOP_APR_CACHE_KEY);
    if (!raw) return null;
    const record: TopAprCacheRecord = JSON.parse(raw);
    if (Date.now() - record.fetchedAt >= TOP_APR_CACHE_TTL_MS) return null;
    return record;
  } catch {
    return null;
  }
};

const writeTopAprCache = (apr: bigint | null) => {
  const record: TopAprCacheRecord = {
    apr: apr == null ? null : apr.toString(),
    fetchedAt: Date.now(),
  };
  localStorage.setItem(TOP_APR_CACHE_KEY, JSON.stringify(record));
};

// Highlights #trade-header-toggle (rendered by Header.tsx) with a one-time
// tour advertising the platform's current top APR. Renders no DOM of its own
// — driver.js appends the popover straight to document.body.
export const AutomatedTradingPromo: FC = () => {
  const navigate = useNavigate();
  const driverRef = useRef<ReturnType<typeof driver>>();

  useEffect(() => {
    if (localStorage.getItem(TOUR_SEEN_KEY)) return;
    let cancelled = false;

    const showTour = (topApr: bigint) => {
      const driverObj = driver({
        popoverClass: "rujira-theme automated-trading-promo",
        stageRadius: 20,
      });
      driverRef.current = driverObj;
      driverObj.highlight({
        element: "#trade-header-toggle",
        popover: {
          title: "Discovered Automated Trading?",
          description: `<div class="flex dir-c gap-1"><div class="automated-trading-promo__visual"><img class="automated-trading-promo__graphic" src="${cclPromo}" width="752" height="334" alt="Concentrated liquidity operating within a changing price range"/><span class="automated-trading-promo__range" aria-hidden="true"></span></div><p class="color-lightGrey">Put your assets to work and earn yield by providing liquidity to the orderbook.</p><button class="outline"><span>See how others are earning up to <br/><strong class="color-teal">${formatBigintPercent(topApr)} APR</strong> providing liquidity!</span><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" aria-hidden="true"><path fill="currentColor" fill-rule="nonzero" d="M427.25 308.65c6.2 6.2 6.2 16.4 0 22.6l-192 192c-6.2 6.2-16.4 6.2-22.6 0-6.2-6.2-6.2-16.4 0-22.6l180.7-180.7-180.7-180.7c-6.2-6.2-6.2-16.4 0-22.6 6.2-6.2 16.4-6.2 22.6 0l192 192Z"/></svg></button><button class="transparent">I've got it, don't show again</button></div>`,
          onPopoverRender: (popover) => {
            const bind = (selector: string, onClick: () => void) =>
              popover.description
                .querySelector(selector)
                ?.addEventListener("click", onClick);

            bind("button.outline", () => {
              driverObj.destroy();
              navigate("/automated-trading");
            });
            bind("button.transparent", () => {
              localStorage.setItem(TOUR_SEEN_KEY, "1");
              driverObj.destroy();
            });
          },
        },
      });
    };

    const cached = readTopAprCache();
    if (cached) {
      if (cached.apr != null) showTour(BigInt(cached.apr));
      return () => {
        cancelled = true;
        driverRef.current?.destroy();
      };
    }

    if (!isAnalyticsConfigured()) return;

    fetchQuery<AutomatedTradingPromoQuery>(
      getAnalyticsEnvironment(),
      topAprQuery,
      {
        minInvestmentAgeDays: MIN_AGE_DAYS,
        minValueUsd: MIN_VALUE_USD_SCALED as unknown as bigint,
      }
    )
      .toPromise()
      .then((result) => {
        if (cancelled) return;

        const topApr =
          result?.finV3.ranges?.edges?.[0]?.node?.analytics?.apr ?? null;
        writeTopAprCache(topApr);
        if (topApr == null) return;
        showTour(topApr);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      driverRef.current?.destroy();
    };
  }, [navigate]);

  return null;
};
